import { jsxs, jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { Box, Heading } from "@zillow/constellation-10";
import { useContext, useRef, useState, useMemo, useEffect } from "react";
import { getYearsAndMonths, currencyFormatter } from "../../RentVsBuyCalculator.mixins.esm.js";
import { trackRentVsBuyView } from "../../RentVsBuyCalculator.tracking.esm.js";
import { calculateFullBreakEven } from "../../RentVsBuyCalculator.utils.esm.js";
import { DataContext } from "../../RentVsBuyDataProvider.esm.js";
import LineChart from "../Chart/Chart.esm.js";
import RentVsBuyGraphOutput from "../GraphOutput/GraphOutput.esm.js";
import ScrubberSlider from "../GraphScrubber/GraphScrubber.esm.js";
const chartDataContent = css.raw({
  display: "flex",
  flexDirection: "column",
  width: "100%"
});
const styledLayoutWidth = css.raw({
  width: "100%"
});
const chartBox = css.raw({
  position: "relative",
  width: "100%"
});
const container = css({
  position: "relative",
  width: "100%"
});
const chart = css({
  width: "100%"
});
const rentVsBuyOutcome = css({
  textAlign: "center",
  padding: "token(spacing.400)",
  backgroundColor: "token(colors.blue.100)",
  borderRadius: "token(radii.md)",
  marginBottom: "token(spacing.200)"
});
const rvbStatementHeading = css.raw({
  textAlign: "center"
});
const styledSlider = css({
  position: "absolute",
  top: "0",
  left: "0",
  width: "100%",
  height: "100%"
});
const ChartContent = () => {
  const {
    currentState
  } = useContext(DataContext);
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [scrubberLine, setScrubberLine] = useState(3);
  const [chartHeight, setChartHeight] = useState(0);
  const chartAreaTopRef = useRef(0);
  const chartAreaBottomRef = useRef(0);
  const chartAreaLeftRef = useRef(0);
  const chartAreaRightRef = useRef(0);
  const [chartAreaTop, setChartAreaTop] = useState(0);
  const [chartAreaBottom, setChartAreaBottom] = useState(0);
  const [chartAreaLeft, setChartAreaLeft] = useState(0);
  const [chartAreaRight, setChartAreaRight] = useState(0);
  const afterDrawPlugin = useMemo(() => ({
    id: "rvb_chartarea",
    afterDraw: (chart2) => {
      const {
        top,
        bottom,
        left,
        right
      } = chart2.chartArea;
      const t = Math.round(top);
      const b = Math.round(bottom);
      const l = Math.round(left);
      const r = Math.round(right);
      if (t !== chartAreaTopRef.current) {
        chartAreaTopRef.current = t;
        setChartAreaTop(t);
      }
      if (b !== chartAreaBottomRef.current) {
        chartAreaBottomRef.current = b;
        setChartAreaBottom(b);
      }
      if (l !== chartAreaLeftRef.current) {
        chartAreaLeftRef.current = l;
        setChartAreaLeft(l);
      }
      if (r !== chartAreaRightRef.current) {
        chartAreaRightRef.current = r;
        setChartAreaRight(r);
      }
    }
  }), []);
  const params = {
    ...currentState,
    loanTermYears: 30,
    buyingUtilities: 300,
    capitalGainsTaxExclusion: 5e5,
    capitalGainsTaxRate: 15,
    initialInflationRate: 0.3,
    longTermHomeAppreciationRate: 4.5,
    longTermInflationRate: 2.4,
    longTermRentAppreciationRate: 3.4,
    rentDepositMonths: 1,
    standardDeduction: 12600
  };
  useEffect(() => {
    trackRentVsBuyView();
  }, []);
  const {
    annualCumulativeCosts,
    breakEvenMonths
  } = useMemo(
    () => calculateFullBreakEven(params),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentState]
  );
  const graphData = {
    annualCumulativeCosts,
    breakEvenMonths,
    purchasePrice: currentState.purchasePrice,
    investmentReturnRate: currentState == null ? void 0 : currentState.investmentReturnRate,
    yearSelected: scrubberLine
  };
  const labels = Array.from({
    length: annualCumulativeCosts.length
  }, (_, index) => index);
  const chartData = {
    labels,
    datasets: [{
      label: "Buy",
      fill: false,
      data: labels.map((n) => {
        var _a, _b;
        return (_b = (_a = annualCumulativeCosts[n]) == null ? void 0 : _a.buyingValues) == null ? void 0 : _b.netCosts;
      }),
      pointRadius: 0,
      backgroundColor: ["#76BA1B"],
      borderColor: "#76BA1B",
      borderWidth: 2,
      datalabels: {
        anchor: "center",
        align: "center",
        offset: 0
      }
    }, {
      label: "Rent",
      fill: false,
      data: labels.map((n) => {
        var _a, _b;
        return (_b = (_a = annualCumulativeCosts[n]) == null ? void 0 : _a.rentingValues) == null ? void 0 : _b.netCosts;
      }),
      pointRadius: 0,
      backgroundColor: ["#006AFF"],
      borderColor: "#006AFF",
      borderWidth: 2,
      datalabels: {
        anchor: "center",
        align: "center",
        offset: 0
      }
    }]
  };
  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      autocolors: false,
      title: {
        display: true,
        text: "Net Costs Over Years"
      },
      legend: {
        display: false
      }
    },
    layout: {
      padding: {
        left: currentState == null ? void 0 : currentState.chartLayout.padding.left,
        right: currentState == null ? void 0 : currentState.chartLayout.padding.right
      }
    },
    scales: {
      x: {
        beginAtZero: false,
        grid: {
          display: false
        },
        ticks: {
          display: true,
          autoSkip: true,
          callback(index) {
            if (index === 9 || index === 19 || index === 29) {
              return (labels[index] + 1).toString();
            }
          }
        }
      },
      y: {
        grace: "20%",
        beginAtZero: false,
        grid: {
          display: false
        },
        ticks: {
          display: true,
          autoSkip: true,
          maxTicksLimit: 8,
          callback(index) {
            if (index < 0) return;
            if (index === 0) return;
            return `$${currencyFormatter(index, 1)}`;
          }
        }
      }
    }
  };
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === chartRef.current) {
          setChartHeight(entry.contentRect.height);
        }
      });
    });
    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
      setChartHeight(chartRef.current.clientHeight);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [chartRef]);
  useEffect(() => {
    let rafId;
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const chart2 = chartInstanceRef.current;
        if (chart2) {
          chart2.stop();
          chart2.resize();
        }
      });
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(rafId);
    };
  }, []);
  const buyVsRentHeader = {
    buy: `After ${getYearsAndMonths(breakEvenMonths)}, buying will be cheaper than renting.`,
    rent: "Buying will never be cheaper than renting."
  };
  const RentVsBuyStatement = () => /* @__PURE__ */ jsx(Heading, { level: 4, css: rvbStatementHeading, "data-testid": "rentvsbuy-statement", children: breakEvenMonths ? buyVsRentHeader.buy : buyVsRentHeader.rent });
  return /* @__PURE__ */ jsxs(Box, { id: "rent-vs-buy-chart-content", css: chartDataContent, children: [
    /* @__PURE__ */ jsx("div", { className: rentVsBuyOutcome, children: /* @__PURE__ */ jsx(RentVsBuyStatement, {}) }),
    /* @__PURE__ */ jsxs(Box, { css: styledLayoutWidth, children: [
      /* @__PURE__ */ jsx(Box, { css: chartBox, children: /* @__PURE__ */ jsxs("div", { className: container, children: [
        /* @__PURE__ */ jsx("div", { id: "rent-vs-buy-chart", ref: chartRef, className: chart, children: /* @__PURE__ */ jsx(LineChart, { chartData, options, chartRef: chartInstanceRef, afterDrawPlugin }) }),
        /* @__PURE__ */ jsx("div", { id: "rent-vs-buy-scrubber", className: styledSlider, children: /* @__PURE__ */ jsx(ScrubberSlider, { onChange: (e) => setScrubberLine(e), id: "rent-vs-buy-chart-scrubber", defaultValue: scrubberLine, chartHeight, chartAreaTop, chartAreaBottom, chartAreaLeft, chartAreaRight }) })
      ] }) }),
      graphData && graphData.annualCumulativeCosts && /* @__PURE__ */ jsx(RentVsBuyGraphOutput, { graphData })
    ] })
  ] });
};
export {
  ChartContent as default
};
